/** 手机触控：独立操作按钮使用至少44px点击区域；桌面保持原紧凑布局，业务权限、提交与跳转逻辑不变。 */
import { ResponsiveTable } from "../mobile/ResponsiveTable";
/** 手机记录列表复用原有数据、勾选与业务操作；普通记录按字段卡片呈现，交互表头和对比矩阵保留横向浏览。 */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { BarChart3, Loader2, Play, Bell, FileDown } from "lucide-react";
import { Button } from "../ui/button";
import { toast } from "../ui/sonner";

const REGION = { la: "洛杉矶", norcal: "北加州", sd: "圣地亚哥", hq: "总部", cn: "中国" };
const pct = (v) => (v == null ? "—" : `${v}%`);
const localDate = (offsetDays = 0) => { const d = new Date(); d.setDate(d.getDate() + offsetDays); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };
const MEDAL = ["🥇", "🥈", "🥉"];

/* 坐席周评报告：立即生成 + 历史周 + 排行 */
export function AgentWeeklyReport({ api, zh }) {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [week, setWeek] = useState(params.get("report") || "");
  const [notify, setNotify] = useState(false);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(!!params.get("report"));

  const load = useCallback(async (w) => {
    try { const { data: d } = await api.get("/telephony/agent-report", { params: w ? { week: w } : {} }); setData(d); }
    catch { setData({ history: [], report: null }); }
  }, [api]);
  useEffect(() => { load(week); }, [load, week]);

  const run = async (offset) => {
    setBusy(true);
    try {
      const { data: d } = await api.post("/telephony/agent-report/run", { week: localDate(offset), notify });
      toast.success(zh ? `已生成 ${d.week} 周评报告${notify ? `，已推送 ${d.notified} 位经理 / VP / CEO（邮件 ${d.emailed || 0}）` : ""}` : `Report ${d.week} generated${notify ? `, notified ${d.notified}` : ""}`);
      setWeek(d.week); params.set("report", d.week); setParams(params, { replace: true }); setOpen(true);
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "生成失败" : "Failed")); }
    setBusy(false);
  };
  const rep = data?.report;
  const weeks = data?.history || [];
  const exportPdf = async () => {
    if (!rep) return;
    try {
      const res = await api.get(`/telephony/agent-report/${rep.id}.pdf`, { params: { lang: zh ? "zh" : "en" }, responseType: "blob" });
      const u = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = u; a.download = `agent_weekly_report_${rep.week}.pdf`; a.click(); URL.revokeObjectURL(u);
      toast.success(zh ? "PDF 已导出" : "PDF exported");
    } catch { toast.error(zh ? "导出失败" : "Export failed"); }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white" data-testid="agent-weekly-report" data-week={rep?.week || ""}>
      <div className="flex items-center gap-2 px-4 py-3 flex-wrap">
        <BarChart3 className="h-4 w-4 text-brand-500" />
        <h2 className="text-sm font-semibold text-slate-800">{zh ? "坐席周评报告" : "Agent weekly report"}</h2>
        <span className="text-xs text-slate-400 hidden md:inline">{zh ? "· 每周一 08:00 自动生成上周并推送销售经理 / VP / CEO（铃铛 + 邮件附 PDF）" : "· auto every Monday 08:00 (bell + email with PDF)"}</span>
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <select value={week} onChange={(e) => { setWeek(e.target.value); setOpen(true); }} data-testid="agent-report-week" className="text-xs rounded-lg border border-slate-200 px-2 py-1.5">
            <option value="">{zh ? "最新一期" : "Latest"}</option>
            {weeks.map((h) => <option key={h.id} value={h.week}>{`${h.week}（${h.start} ~ ${h.end}）`}</option>)}
          </select>
          <label className="inline-flex items-center gap-1 text-xs text-slate-500 cursor-pointer" data-testid="agent-report-notify" title={zh ? "生成后同时推送铃铛 + 邮件" : "Also push bell + email"}>
            <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} /><Bell className="h-3 w-3" />{zh ? "同时推送" : "Notify"}
          </label>
          {rep && <Button size="sm" variant="outline" data-testid="agent-report-pdf" onClick={exportPdf}><FileDown className="h-4 w-4 mr-1" />PDF</Button>}
          <Button size="sm" variant="outline" data-testid="agent-report-run-last" disabled={busy} onClick={() => run(-7)}>{zh ? "生成上周" : "Last week"}</Button>
          <Button size="sm" data-testid="agent-report-run" disabled={busy} onClick={() => run(0)} className="bg-brand-500 hover:bg-brand-600 text-white">
            {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Play className="h-4 w-4 mr-1" />}{zh ? "立即生成本周报告" : "Generate this week"}
          </Button>
          <button data-testid="agent-report-toggle" onClick={() => setOpen((o) => !o)} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-xs text-slate-500 hover:text-slate-800">{open ? (zh ? "收起" : "Hide") : (zh ? "展开" : "Show")}</button>
        </div>
      </div>
      {open && (
        !rep ? (
          <p className="px-4 pb-4 text-sm text-slate-400" data-testid="agent-report-empty">{zh ? "尚无报告，点击「立即生成本周报告」或等待周一自动生成。" : "No report yet."}</p>
        ) : (
          <div className="border-t border-slate-100">
            <p className="px-4 py-2 text-xs text-slate-500" data-testid="agent-report-totals">
              {rep.week} · {rep.start} ~ {rep.end} · {rep.regionLabel ? `${rep.regionLabel} · ` : ""}{zh ? `${rep.totals.active} / ${rep.totals.agents} 位坐席有通话 · 共 ${rep.totals.calls} 通 · 平均接通率 ${pct(rep.totals.avgAnswerRate)} · 平均 AI 评分 ${rep.totals.avgScore ?? "—"}` : `${rep.totals.active}/${rep.totals.agents} active · ${rep.totals.calls} calls`}
              {rep.notifiedAt && <span className="ml-2 text-emerald-600" data-testid="agent-report-notified">{zh ? `已推送 ${rep.recipients?.length || 0} 人` : `Notified ${rep.recipients?.length || 0}`}</span>}
            </p>
            <div className="overflow-x-auto">
              <ResponsiveTable className="w-full text-sm" data-testid="agent-report-table">
                <thead className="bg-slate-50 text-slate-500 text-xs">
                  <tr>{[zh ? "排名" : "#", zh ? "坐席" : "Agent", zh ? "分公司" : "Branch", zh ? "通话" : "Calls", zh ? "接通率" : "Answer", zh ? "AI 评分" : "AI", zh ? "经理评分" : "Stars", zh ? "回拨完成率" : "Callback", zh ? "综合" : "Total"].map((h) => <th key={h} className="px-3 py-2 text-left font-medium">{h}</th>)}</tr>
                </thead>
                <tbody>
                  {rep.rows.map((r) => (
                    <tr key={r.username} data-testid={`agent-report-row-${r.username}`} data-rank={r.rank ?? ""} className="border-t border-slate-100 hover:bg-slate-50/60">
                      <td className="px-3 py-2 font-bold">{r.rank ? (MEDAL[r.rank - 1] || r.rank) : <span className="text-slate-300">—</span>}</td>
                      <td className="px-3 py-2"><button onClick={() => navigate(`/agents/${r.username}`)} className="font-semibold text-slate-800 hover:text-red-700 hover:underline">{r.name}</button></td>
                      <td className="px-3 py-2 text-slate-500">{REGION[r.region] || r.region || "—"}</td>
                      <td className="px-3 py-2">{r.calls}</td>
                      <td className="px-3 py-2">{pct(r.answerRate)}</td>
                      <td className="px-3 py-2">{r.avgScore ?? "—"}</td>
                      <td className="px-3 py-2">{r.avgStars != null ? `${r.avgStars} ★` : "—"}</td>
                      <td className="px-3 py-2">{pct(r.callbackRate)}</td>
                      <td className="px-3 py-2 font-bold text-slate-900">{r.composite ?? "—"}</td>
                    </tr>
                  ))}
                  {rep.rows.length === 0 && <tr><td colSpan={9} className="text-center text-slate-300 py-6">{zh ? "本区域无坐席" : "No agents"}</td></tr>}
                </tbody>
              </ResponsiveTable>
            </div>
            <p className="px-4 py-2 text-[11px] text-slate-400">{zh ? "综合 = 接通率 / AI 话术评分 / 经理评分（星级 ÷ 5）/ 回拨完成率 中有数据项的平均；无通话的坐席不参与排名。" : "Total = mean of answer rate, AI score, stars/5, callback rate."}</p>
            {rep.managers?.length > 0 && (
              <div className="border-t border-slate-100" data-testid="agent-report-managers">
                <p className="px-4 py-2 text-xs font-semibold text-slate-700">{zh ? "经理评分完成率（本周）" : "Manager rating completion (this week)"}
                  {rep.managerTotals && <span className="ml-2 font-normal text-slate-500" data-testid="agent-report-manager-totals">{zh ? `有录音通话 ${rep.managerTotals.candidates} 通 · 已评 ${rep.managerTotals.rated} · 完成率 ${rep.managerTotals.completionRate ?? "—"}${rep.managerTotals.completionRate != null ? "%" : ""}` : `${rep.managerTotals.rated}/${rep.managerTotals.candidates} rated`}</span>}
                </p>
                <div className="overflow-x-auto">
                  <ResponsiveTable className="w-full text-sm min-w-[640px]">
                    <thead className="bg-slate-50 text-slate-500 text-xs"><tr>{[zh ? "分公司" : "Branch", zh ? "销售经理" : "Manager", zh ? "有录音通话" : "Recorded", zh ? "已评" : "Rated", zh ? "完成率" : "Completion", zh ? "平均星级" : "Avg ★", zh ? "超 3 天" : "Overdue", zh ? "催办" : "Nudges"].map((h, i) => <th key={i} className="px-3 py-2 text-left font-medium">{h}</th>)}</tr></thead>
                    <tbody>
                      {rep.managers.map((m) => (
                        <tr key={`${m.region}-${m.username || "none"}`} data-testid={`agent-report-manager-${m.username || m.region}`} className="border-t border-slate-100">
                          <td className="px-3 py-1.5 text-slate-600">{m.regionLabel}</td>
                          <td className="px-3 py-1.5 font-semibold text-slate-800">{m.name || <span className="text-red-500 font-normal">{zh ? "未设销售经理" : "No manager"}</span>}</td>
                          <td className="px-3 py-1.5">{m.candidates}</td>
                          <td className="px-3 py-1.5">{m.rated}</td>
                          <td className={`px-3 py-1.5 font-semibold ${m.completionRate == null ? "text-slate-300" : m.completionRate >= 80 ? "text-emerald-700" : "text-red-600"}`}>{m.completionRate == null ? "—" : `${m.completionRate}%`}</td>
                          <td className="px-3 py-1.5">{m.avgStars != null ? `${m.avgStars} ★` : "—"}</td>
                          <td className="px-3 py-1.5">{m.overdue || 0}</td>
                          <td className="px-3 py-1.5">{m.username ? m.nudges || 0 : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </ResponsiveTable>
                </div>
              </div>
            )}
          </div>
        )
      )}
    </div>
  );
}
